import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Copy, Check, Wallet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==== EDIT THESE WITH YOUR REAL DETAILS ====
const ZELLE_HANDLE = "+1 (408) 438-5390"; // Zelle registered to this phone number
const ZELLE_DISPLAY_NAME = "Shemushi LLC";
const CONTACT_EMAIL = "morenavita01@gmail.com";
const CONTACT_PHONE = "+1 (408) 438-5390";
// ============================================

const tracks = [
  { id: "sprouts", label: "Sprouts", band: "Ages 5 – 8", time: "2:00 – 3:00 PM" },
  { id: "explorers", label: "Explorers", band: "Ages 9 – 12", time: "3:30 – 4:30 PM" },
  { id: "navigators", label: "Navigators", band: "Ages 13 – 18", time: "5:00 – 6:00 PM" },
];

function CopyRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch {
          /* no-op */
        }
      }}
      className="w-full text-left flex items-center justify-between gap-4 py-3 border-b border-white/20 group"
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="font-mono-editorial text-[10px] tracking-[0.28em] text-white/50">{label}</div>
        <div className="font-serif-editorial text-lg md:text-xl text-white truncate">{value}</div>
      </div>
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-mono-editorial tracking-[0.24em] px-2.5 py-1 rounded-full border border-white/30 flex-shrink-0 transition-colors ${
          copied ? "bg-white text-[color:var(--ink)] border-white" : "text-white/70 group-hover:border-white group-hover:text-white"
        }`}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? "COPIED" : "COPY"}
      </span>
    </button>
  );
}

export default function EnrollCTA() {
  const [form, setForm] = useState({
    child_name: "",
    child_age: "",
    track: "",
    notes: "",
    parent_name: "",
    email: "",
    phone: "",
    city: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isValid =
    form.child_name.trim() &&
    form.child_age.trim() &&
    form.track &&
    form.parent_name.trim() &&
    form.email.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      toast.error("Please fill child name, age, batch, parent name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enroll`, form);
      setDone(true);
      toast.success("Enrollment received. Complete payment via Zelle to confirm your seat.");
    } catch {
      toast.error(`Could not submit. Please email ${CONTACT_EMAIL}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enroll" className="ink-bg text-[color:var(--inv)] py-24 md:py-40" data-testid="enroll-section">
      <Toaster position="bottom-center" richColors />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="grid grid-cols-12 gap-6 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
              Enroll into <em className="font-serif-editorial italic" style={{ color: "var(--brand)" }}>Inner Compass</em>.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 self-end text-white/70 leading-relaxed">
            Share your child&rsquo;s details, choose a batch, and complete the first payment via Zelle. A founder will confirm your seat within 48 hours.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {/* ============ FORM ============ */}
          <form
            onSubmit={submit}
            className="col-span-12 lg:col-span-7 border border-white/20 p-8 md:p-12"
            data-testid="enroll-form"
          >
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="enroll-success"
              >
                <span className="font-mono-editorial text-[11px] tracking-[0.28em]" style={{ color: "var(--brand)" }}>
                  — Received
                </span>
                <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02]">
                  Thank you, {form.parent_name.split(" ")[0]}.
                </h3>
                <p className="mt-6 text-white/75 leading-relaxed max-w-[52ch]">
                  We&rsquo;ve received {form.child_name}&rsquo;s details for the{" "}
                  <b className="text-white">
                    {tracks.find((t) => t.id === form.track)?.label}
                  </b>{" "}
                  batch. To confirm the seat, please complete the first payment via <b className="text-white">Zelle</b>{" "}
                  using the details on the right. A founder will reach out to <b className="text-white">{form.email}</b>{" "}
                  within 48 hours.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setDone(false);
                    setForm({
                      child_name: "",
                      child_age: "",
                      track: "",
                      notes: "",
                      parent_name: "",
                      email: "",
                      phone: "",
                      city: "",
                    });
                  }}
                  className="mt-8 inline-flex items-center gap-2 text-white/70 hover:text-white text-sm"
                  data-testid="enroll-reset-btn"
                >
                  ← Enroll another child
                </button>
              </motion.div>
            ) : (
              <>
                {/* --- Student --- */}
                <div className="mb-10">
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="font-mono-editorial text-[10px] tracking-[0.32em] text-white/50">01</span>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight">Student information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Child's full name" required>
                      <input
                        type="text"
                        value={form.child_name}
                        onChange={set("child_name")}
                        className="input"
                        data-testid="child-name-input"
                        placeholder="e.g. Ananya Rao"
                      />
                    </Field>
                    <Field label="Child's age" required>
                      <input
                        type="number"
                        min="5"
                        max="18"
                        value={form.child_age}
                        onChange={set("child_age")}
                        className="input"
                        data-testid="child-age-input"
                        placeholder="5 – 18"
                      />
                    </Field>
                  </div>

                  <div className="mt-6">
                    <Field label="Choose batch" required>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {tracks.map((t) => (
                          <button
                            type="button"
                            key={t.id}
                            onClick={() => setForm((f) => ({ ...f, track: t.id }))}
                            className={`text-left px-4 py-3 border transition-colors duration-300 ${
                              form.track === t.id
                                ? "bg-[color:var(--brand)] border-[color:var(--brand)] text-white"
                                : "border-white/25 text-white/80 hover:border-white/60"
                            }`}
                            data-testid={`track-btn-${t.id}`}
                          >
                            <span className="block font-mono-editorial text-[10px] tracking-[0.22em] opacity-70">
                              {t.band}
                            </span>
                            <span className="block font-display italic text-xl">{t.label}</span>
                            <span className="block font-mono-editorial text-[10px] tracking-[0.2em] opacity-70 mt-0.5">
                              {t.time}
                            </span>
                          </button>
                        ))}
                      </div>
                    </Field>
                  </div>

                  <div className="mt-6">
                    <Field label="Anything we should know? (allergies, hopes, questions)">
                      <textarea
                        rows={3}
                        value={form.notes}
                        onChange={set("notes")}
                        className="input resize-none"
                        data-testid="notes-input"
                      />
                    </Field>
                  </div>
                </div>

                {/* --- Contact --- */}
                <div className="mb-8">
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="font-mono-editorial text-[10px] tracking-[0.32em] text-white/50">02</span>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight">Parent contact details</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Parent's full name" required>
                      <input
                        type="text"
                        value={form.parent_name}
                        onChange={set("parent_name")}
                        className="input"
                        data-testid="parent-name-input"
                      />
                    </Field>
                    <Field label="Email" required>
                      <input
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        className="input"
                        data-testid="email-input"
                        placeholder="you@family.com"
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        className="input"
                        data-testid="phone-input"
                        placeholder="+1 …"
                      />
                    </Field>
                    <Field label="City">
                      <input
                        type="text"
                        value={form.city}
                        onChange={set("city")}
                        className="input"
                        data-testid="city-input"
                        placeholder="San Antonio"
                      />
                    </Field>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || !isValid}
                  className="inline-flex items-center gap-3 h-12 md:h-14 px-6 rounded-full bg-[color:var(--brand)] text-white disabled:opacity-40 hover:bg-white hover:text-[color:var(--ink)] transition-colors duration-500"
                  data-testid="enroll-submit-btn"
                >
                  {submitting ? "Sending…" : "Submit enrollment"} <span>→</span>
                </button>

                <p className="mt-4 text-xs text-white/50">
                  Your seat is confirmed once we receive your first Zelle payment.
                </p>
              </>
            )}
          </form>

          {/* ============ ZELLE PAYMENT CARD ============ */}
          <aside
            className="col-span-12 lg:col-span-5 relative overflow-hidden border-2 p-8 md:p-10 flex flex-col"
            style={{ background: "#0a0b0f", borderColor: "var(--brand)" }}
            data-testid="zelle-panel"
          >
            <div className="flex items-center gap-3 mb-2">
              <Wallet size={18} strokeWidth={1.75} />
              <span className="font-mono-editorial text-[10px] tracking-[0.32em] text-white/60">
                PAYMENT · ZELLE
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl leading-[1.02] tracking-tight">
              Send via <em className="font-serif-editorial italic" style={{ color: "var(--brand)" }}>Zelle</em>.
            </h3>
            <p className="mt-4 text-white/70 text-sm leading-relaxed max-w-[36ch]">
              Zelle is instant, free, and available inside most US bank apps. Send to the details below to reserve your child&rsquo;s seat.
            </p>

            {/* First payment breakdown */}
            <div className="mt-8">
              <div className="font-mono-editorial text-[10px] tracking-[0.32em] text-white/50 mb-2">
                FIRST PAYMENT
              </div>
              <div className="flex items-baseline gap-3">
                <span className="font-display text-7xl md:text-8xl leading-none tracking-tighter">$205</span>
                <span className="font-serif-editorial italic text-white/70">
                  first quarter + one-time registration
                </span>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-white/70">
                <div className="flex justify-between border-b border-white/15 pb-1.5">
                  <span>Quarterly commitment (3 × $60)</span>
                  <span className="text-white">$180</span>
                </div>
                <div className="flex justify-between border-b border-white/15 pb-1.5">
                  <span>One-time registration</span>
                  <span className="text-white">$25</span>
                </div>
              </div>
              <div className="mt-3 text-xs text-white/50">
                Following quarters: $180 (billed quarterly).
              </div>
            </div>

            {/* Zelle handle rows */}
            <div className="mt-10">
              <CopyRow label="Zelle to" value={ZELLE_HANDLE} testid="zelle-handle" />
              <CopyRow label="Recipient name" value={ZELLE_DISPLAY_NAME} testid="zelle-name" />
            </div>

            {/* Memo note */}
            <div
              className="mt-6 p-4 border-l-2"
              style={{ borderColor: "var(--brand)", background: "rgba(217,93,57,0.08)" }}
            >
              <div className="font-mono-editorial text-[10px] tracking-[0.28em] text-white/60">MEMO / NOTE</div>
              <div className="text-sm text-white mt-1">
                Include your child&rsquo;s full name in the Zelle memo so we can match your payment to the enrollment form.
              </div>
            </div>

            {/* Contact fallback */}
            <div className="mt-auto pt-8">
              <div className="font-mono-editorial text-[10px] tracking-[0.32em] text-white/50 mb-3">
                QUESTIONS?
              </div>
              <div className="space-y-1 text-sm text-white/80">
                <div>
                  Email · <a href={`mailto:${CONTACT_EMAIL}`} className="link-underline text-white">{CONTACT_EMAIL}</a>
                </div>
                <div>
                  Phone · <a href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`} className="link-underline text-white">{CONTACT_PHONE}</a>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(255,255,255,0.25);
          padding: 12px 0;
          color: #F7F5F0;
          font-family: inherit;
          font-size: 16px;
          outline: none;
          transition: border-color 0.4s;
        }
        .input:focus { border-color: #D95D39; }
        .input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono-editorial text-[10px] tracking-[0.28em] text-white/50">
        {label} {required && <span style={{ color: "var(--brand)" }}>*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
